import React, { useEffect, useState, useCallback } from 'react';
import AddCustomerModal from '../modals/AddCustomerModal';
import AddOrderModal from '../modals/AddOrderModal';
import AddCreditModal from '../modals/AddCreditModal';
import { useParams } from 'react-router-dom';
import { Customer, Order, CustomerCreditSummary } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import FullscreenLoader from '../components/FullScreenLoader';
import OrdersTable from '../components/OrdersTable';
import ConfirmationModal from '../modals/ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { FEATURE_FLAGS } from '../constants/featureFlags';
import CustomerReferrals, { InviteCodeLine, ReferredByLine, hasReferralActivity } from '../components/CustomerReferrals';

const CustomerDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const showDeltaPay = useFeatureFlag(FEATURE_FLAGS.DELTA_PAY);
  const referralProgrammeOn = useFeatureFlag(FEATURE_FLAGS.REFERRAL_PROGRAMME);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState(false);
  const [duplicateSourceOrder, setDuplicateSourceOrder] = useState<Order | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isAddCreditModalOpen, setIsAddCreditModalOpen] = useState(false);
  const [isCreditExpanded, setIsCreditExpanded] = useState(false);
  const [customerCredit, setCustomerCredit] = useState<CustomerCreditSummary | null>(null);

  const fetchCredit = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`/customers/${id}/credits`);
      const data = await res.json();
      setCustomerCredit(data);
    } catch (error) {
      console.error('Error fetching customer credit:', error);
    }
  }, [id]);

  const fetchCustomerAndOrders = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    try {
      const customerRes = await fetchWithAuth(`/customers/${id}`);
      const customerData = await customerRes.json();
      setCustomer(customerData);

      const ordersRes = await fetchWithAuth(`/orders?customer_id=${id}&page=${page}&include_test=true`);
      const ordersData = await ordersRes.json();
      setOrders(ordersData.orders);
      setTotalPages(ordersData.total_pages);
      setCurrentPage(ordersData.current_page);
    } catch (error) {
      console.error('Error fetching customer or orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomerAndOrders();
    fetchCredit();
  }, [fetchCustomerAndOrders, fetchCredit]);

  const handleDelete = async () => {
    setIsConfirmModalOpen(false);
    try {
      const response = await fetchWithAuth(`/customers/${id}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Customer deleted successfully.');
        navigate(`/customers`);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to delete customer: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('An error occurred while deleting the customer.');
    }
  };

  if (isLoading) {
    return <FullscreenLoader message="Loading Customer..." />;
  }

  if (!customer) {
    return <div className="p-6">Customer not found.</div>;
  }

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Customer Details</h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative group w-full sm:w-auto">
            <button
              onClick={() => setIsConfirmModalOpen(true)}
              disabled={orders.length > 0}
              className="bg-red-600 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              Delete Customer
            </button>
            {orders.length > 0 && (
              <div className="absolute bottom-full mb-2 w-64 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                Cannot delete a customer with existing orders.
              </div>
            )}
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
          >
            Edit Customer
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-md p-4 space-y-2 text-sm text-gray-700 mb-6">
        <p><strong>Name:</strong> {customer.full_name}</p>
        <p><strong>ID:</strong> {customer.code}</p>
        <p><strong>Phone:</strong> {customer.cell_number}</p>
        <p><strong>Email:</strong> {customer.email}</p>
        <p><strong>Source:</strong> {customer.source || 'N/A'}</p>
        <p><strong>Origin:</strong> {customer.origin ? customer.origin.replace(/_/g, ' ').toUpperCase() : 'N/A'}</p>
        {showDeltaPay && (
          <p><strong>DeltaPay Username:</strong> {customer.delta_pay_username || 'N/A'}</p>
        )}
        <p><strong>Collection Preference:</strong> {customer.collection_area_preference || 'N/A'}</p>
        <p>
          <strong>Credit Balance:</strong>{' '}
          <span className={(customer.credit_balance ?? 0) > 0 ? 'text-green-600 font-semibold' : 'text-gray-500'}>
            {formatCurrency(customer.credit_balance ?? 0)}
          </span>
        </p>
        <InviteCodeLine customer={customer} showCopyLink={referralProgrammeOn} />
        {(referralProgrammeOn || customer.referred_by) && <ReferredByLine customer={customer} />}
        <div className="flex items-center gap-2">
          <strong>Verified:</strong>
          <div className="relative group inline-block">
            <span
              className={`inline-block w-3 h-3 rounded-full ${
                customer.phone_verified_at ? 'bg-green-500' : 'bg-red-500'
              }`}
            ></span>
            <div className="absolute bottom-full mb-2 w-64 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {customer.phone_verified_at
                ? `Phone verified on ${new Date(customer.phone_verified_at).toLocaleDateString()}`
                : 'Phone not verified'}
            </div>
          </div>
        </div>
        <br/>
        <div className="flex items-center gap-2">
        <strong>Whatsapp Deliverability:</strong>
        <div className="relative group inline-block">
          <span
            className={`inline-block w-3 h-3 rounded-full ${
              customer.deliverability_status === 'successful'
                ? 'bg-green-500'
                : customer.deliverability_status === 'failed'
                ? 'bg-red-500'
                : 'bg-amber-400'
            }`}
          ></span>
          <div className="absolute bottom-full mb-2 w-64 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            {customer.deliverability_status === 'successful'
              ? 'Last Whatsapp message sent successfully'
              : customer.deliverability_status === 'failed'
              ? 'Last Whatsapp message failed'
              : 'No Whatsapp messages sent'}
          </div>
        </div>
</div>
      </div>

      {(referralProgrammeOn || hasReferralActivity(customer)) && (
        <CustomerReferrals
          customer={customer}
          canGrant={referralProgrammeOn}
          onGranted={() => fetchCustomerAndOrders(currentPage)}
        />
      )}

      <div className="mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
          <button
            onClick={() => setIsCreditExpanded(!isCreditExpanded)}
            className="flex items-center gap-2 text-left"
          >
            <h3 className="text-lg font-semibold">
              Credit History
              {customerCredit && customerCredit.transactions.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-1">
                  ({customerCredit.transactions.length})
                </span>
              )}
            </h3>
            <svg
              className={`w-5 h-5 text-gray-500 transform transition-transform ${isCreditExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={() => setIsAddCreditModalOpen(true)}
            className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
          >
            + Add Credit
          </button>
        </div>
        {isCreditExpanded && (
          <div className="bg-white shadow rounded-md overflow-x-auto">
            {customerCredit && customerCredit.transactions.length > 0 ? (
              <table className="min-w-full text-sm text-gray-700">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3 hidden md:table-cell">Balance Before</th>
                    <th className="px-4 py-3">Balance After</th>
                    <th className="px-4 py-3 hidden md:table-cell">Notes</th>
                    <th className="px-4 py-3 hidden md:table-cell">Added By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {customerCredit.transactions.map(tx => (
                    <tr key={tx.id}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          tx.transaction_type === 'credit_added'
                            ? 'bg-green-100 text-green-700'
                            : tx.transaction_type === 'credit_applied'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {tx.transaction_type === 'credit_added' ? 'Added'
                            : tx.transaction_type === 'credit_applied' ? 'Applied'
                            : 'Reversed'}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium">{formatCurrency(tx.amount)}</td>
                      <td className="px-4 py-3 hidden md:table-cell">{formatCurrency(tx.balance_before)}</td>
                      <td className="px-4 py-3">{formatCurrency(tx.balance_after)}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-500">{tx.notes || '—'}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-gray-500">{tx.created_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="px-4 py-6 text-sm text-gray-500">There is no credit history for this customer.</p>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h3 className="text-lg font-semibold">Orders</h3>
        <button
          onClick={() => {
            setSelectedOrder(null);
            setDuplicateSourceOrder(null);
            setIsAddOrderModalOpen(true);
          }}
          className="bg-black text-white px-4 py-2 rounded-md font-semibold hover:bg-gray-800 w-full sm:w-auto"
        >
          + Add Order
        </button>
      </div>
      <OrdersTable
        orders={orders}
        openActionMenuId={openActionMenuId}
        setOpenActionMenuId={setOpenActionMenuId}
        onEdit={(order) => {
          setDuplicateSourceOrder(null);
          setSelectedOrder(order);
          setIsAddOrderModalOpen(true);
        }}
        onDuplicate={(order) => {
          setSelectedOrder(null);
          setDuplicateSourceOrder(order);
          setIsAddOrderModalOpen(true);
        }}
        onSuccess={() => fetchCustomerAndOrders(currentPage)}
      />

      <div className="flex justify-center items-center space-x-4 mt-4">
        <button
          disabled={currentPage === 1}
          onClick={() => fetchCustomerAndOrders(currentPage - 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Previous
        </button>
        <span>Page {currentPage} of {totalPages}</span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => fetchCustomerAndOrders(currentPage + 1)}
          className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
        >
          Next
        </button>
      </div>

      <AddCreditModal
        isOpen={isAddCreditModalOpen}
        onClose={() => setIsAddCreditModalOpen(false)}
        onSuccess={() => {
          setIsAddCreditModalOpen(false);
          fetchCustomerAndOrders(currentPage);
          fetchCredit();
        }}
        customerId={customer.id}
        customerName={customer.full_name}
      />
      <AddCustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          setIsModalOpen(false);
          fetchCustomerAndOrders(currentPage);
        }}
        selectedCustomer={customer}
        showDeltaPay={showDeltaPay}
      />
      <AddOrderModal
        isOpen={isAddOrderModalOpen}
        onClose={() => {
          setIsAddOrderModalOpen(false);
          setSelectedOrder(null);
          setDuplicateSourceOrder(null);
        }}
        onSuccess={() => {
          setIsAddOrderModalOpen(false);
          setSelectedOrder(null);
          setDuplicateSourceOrder(null);
          fetchCustomerAndOrders(currentPage);
        }}
        order={selectedOrder}
        duplicateFromOrder={duplicateSourceOrder}
        disableCustomerSelect={true}
        customer={{label: customer.full_name, value: customer.id.toString()}}
      />
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleDelete}
        onCancel={() => setIsConfirmModalOpen(false)}
        message="Are you sure you want to delete this customer?"
      />
    </div>
  );
};

export default CustomerDetails;
